import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "./i18n";

/**
 * Per-page SEO. Pass i18n keys for title/description.
 * Updates <title>, <html lang>, meta description, OG/Twitter tags, and canonical.
 */
export function useSEO({ titleKey, descriptionKey, image }) {
  const { t, lang } = useI18n();
  const location = useLocation();

  useEffect(() => {
    const title = titleKey ? t(titleKey) : "LocalH2O";
    const description = descriptionKey ? t(descriptionKey) : "";
    const ogImage =
      image ||
      (lang === "pl" ? "/brand/logo-pl.png" : "/brand/logo-en.jpg");
    const url =
      typeof window !== "undefined"
        ? `${window.location.origin}${location.pathname}`
        : "";

    document.title = title;
    document.documentElement.lang = lang === "pl" ? "pl" : "en";

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace(/[[\]"]/g, "").split("=");
        el.setAttribute(k, v);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:locale"]', "content", lang === "pl" ? "pl_PL" : "en_GB");
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[name="twitter:image"]', "content", ogImage);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
  }, [t, lang, titleKey, descriptionKey, image, location.pathname]);
}
